import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
    Box, Paper, Typography, Divider, Button, Grid, 
    IconButton, Stack, CircularProgress
} from '@mui/material';
import {
    LocalPrintshop as LocalPrintshopIcon,
    AirplayOutlined as AirplayOutlinedIcon,
    FileDownloadOutlined as FileDownloadOutlinedIcon,
    SyncAltOutlined as SwitchIcon,
    SettingsOutlined as SettingsIcon
} from '@mui/icons-material';
import { LABELGENAPI } from '../../../API'; // Adjust this path match to point to your axios instance
import { setQzStateInfo, setQzNotifications, setQzInProgress } from '../../../qzSlice'; // Adjust this path to your slice location

const qz = require("qz-tray");

// --- Enterprise Styled Components ---
const SettingsContainer = styled(Paper)(({ theme }) => ({
    borderRadius: '16px',
    border: 'none',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    padding: theme.spacing(4),
    backgroundColor: '#FFFFFF',
    position: 'relative'
}));

const StatusBlock = styled(Box)(({ theme, isActive }) => ({
    padding: theme.spacing(3),
    borderRadius: '16px',
    border: '1px solid',
    borderColor: isActive ? theme.palette.success.light : theme.palette.info.light,
    backgroundColor: isActive ? 'rgba(46, 125, 50, 0.04)' : 'rgba(2, 136, 209, 0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
}));

const SectionHeader = ({ icon, title, subtitle }) => (
    <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ p: 1.5, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: '12px', color: 'primary.main' }}>
            {icon}
        </Box>
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B', lineHeight: 1.2 }}>
                {title}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                {subtitle}
            </Typography>
        </Box>
    </Box>
);

const QZTrayPage = () => {
    const dispatch = useDispatch();
    const qzState = useSelector(state => state.qz);
    const [localLoading, setLocalLoading] = useState(false);

    // --- Isolated Security Handshake Routine ---
    const verifyQzSecuritySignature = () => {
        qz.security.setCertificatePromise((resolve, reject) => {
            LABELGENAPI.get("/security/digital-certificate")
                .then(response => resolve(response.data))
                .catch(error => reject(error.response));
        });
        
        qz.security.setSignatureAlgorithm("SHA512");
        qz.security.setSignaturePromise((toSign) => {
            return (resolve, reject) => {
                LABELGENAPI.get("/security/private-key?data=" + toSign)
                    .then(response => resolve(response.data))
                    .catch(error => reject(error.response));
            };
        });
    };

    // --- Core Connection Engine ---
    const handleConnectQZ = () => {
        if (qz.websocket.isActive()) {
            return;
        }

        setLocalLoading(true);
        dispatch(setQzInProgress());
        verifyQzSecuritySignature();

        qz.websocket.connect()
            .then(() => {
                dispatch(setQzStateInfo({
                    "is_active": true,
                    "qz": qz,
                    "printer": ""
                }));

                qz.websocket.setClosedCallbacks(() => {
                    dispatch(setQzStateInfo({
                        "is_active": false,
                        "printer": "",
                        "qz": ""
                    }));
                });

                return qz.printers.getDefault();
            })
            .then((printer) => {
                if (printer && printer !== "undefined" && typeof printer === "string" && printer.length !== 0) {
                    dispatch(setQzStateInfo({
                        "is_active": true,
                        "printer": printer,
                        "qz": qz
                    }));
                }
            })
            .catch((e) => {
                dispatch(setQzStateInfo({
                    "is_active": false,
                    "printer": "",
                    "qz": ""
                }));
                
                dispatch(setQzNotifications({
                    "message": ["Unable to establish connection with local QZ Tray client. Please ensure the desktop app is open and running."],
                    "error": true
                }));
                console.warn("QZ Tray Client connection blocked safely:", e.message);
            })
            .finally(() => {
                setLocalLoading(false);
            });
    };

    // --- Core Disconnect Engine ---
    const handleDisconnectQZ = () => {
        if (qzState.queue_count > 0) {
            dispatch(setQzNotifications({
                "message": ["Please wait for pending queues to be printed"],
                "error": true
            }));
            return;
        }

        if (qz.websocket.isActive()) {
            setLocalLoading(true);
            qz.websocket.disconnect()
                .then(() => {
                    dispatch(setQzStateInfo({
                        "is_active": false,
                        "printer": "",
                        "qz": ""
                    }));
                })
                .catch((e) => {
                    dispatch(setQzNotifications({
                        "message": [e.message],
                        "error": true
                    }));
                })
                .finally(() => {
                    setLocalLoading(false);
                });
        } else {
            // Force reset state if local socket was closed un-intentionally
            dispatch(setQzStateInfo({
                "is_active": false,
                "printer": "",
                "qz": ""
            }));
        }
    };

    // Keep active socket settings verified with Redux states on configuration changes
    useEffect(() => {
        if (qz.websocket.isActive() && !qzState.is_active) {
            dispatch(setQzStateInfo({
                "is_active": true,
                "qz": qz,
                "printer": qzState.printer || ""
            }));
        }
    }, [qzState.is_active, dispatch]);

    const isWorking = localLoading || qzState.progress;

    return (
        <Box sx={{ p: 1 }}>
            <SettingsContainer elevation={0}>
                <SectionHeader 
                    icon={<SettingsIcon />} 
                    title="Printing Configuration" 
                    subtitle="Manage your local printing environment and QZ Tray integration for direct label output" 
                />
                
                <Divider sx={{ mb: 5, borderColor: '#F1F5F9' }} />

                <Grid container justifyContent="center">
                    <Grid item xs={12} md={10} lg={8}>
                        <Stack spacing={3}>
                            
                            {/* 1. Status Section (Active or Inactive) */}
                            <StatusBlock isActive={qzState.is_active}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{ 
                                        p: 1.5, 
                                        bgcolor: qzState.is_active ? 'success.main' : 'info.main', 
                                        borderRadius: '12px', 
                                        color: '#FFF',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {isWorking ? <CircularProgress size={24} color="inherit" /> : <LocalPrintshopIcon />}
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E293B' }}>
                                            {qzState.is_active ? "QZ Mode Enabled" : "Browser Mode Active"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                                            {qzState.is_active ? (
                                                <>
                                                    Printer: {qzState.printer || "Ready"} 
                                                    {qzState.queue_count >= 0 && ` • Queue: ${qzState.queue_count} jobs`}
                                                </>
                                            ) : (
                                                "Using standard browser-based printing dialogs"
                                            )}
                                        </Typography>
                                    </Box>
                                </Stack>
                                
                                <Button 
                                    variant="contained" 
                                    size="small"
                                    startIcon={!isWorking && <SwitchIcon />}
                                    onClick={qzState.is_active ? handleDisconnectQZ : handleConnectQZ}
                                    disabled={isWorking}
                                    sx={{ 
                                        borderRadius: '10px', 
                                        textTransform: 'none', 
                                        fontWeight: 700,
                                        bgcolor: '#1E293B',
                                        '&:hover': { bgcolor: '#0F172A' },
                                        minWidth: '140px'
                                    }}
                                >
                                    {isWorking ? "Processing..." : (qzState.is_active ? "Switch to Browser" : "Switch to QZ")}
                                </Button>
                            </StatusBlock>

                            {/* 2. Download Section */}
                            <Box 
                                component="a" 
                                href="https://qz.io/download/" 
                                target="_blank"
                                sx={{ 
                                    textDecoration: 'none',
                                    display: 'block',
                                    p: 3,
                                    borderRadius: '16px',
                                    border: '1px dashed #E2E8F0',
                                    transition: 'all 0.2s',
                                    '&:hover': { bgcolor: '#F8FAFC', borderColor: 'primary.main' }
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ color: 'primary.main', display: 'flex' }}>
                                            <AirplayOutlinedIcon fontSize="large" />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E293B' }}>
                                                Download QZ Tray
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                                                Direct thermal printing requires the latest version of QZ Tray installed locally.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <IconButton size="medium" sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', color: 'primary.main' }}>
                                        <FileDownloadOutlinedIcon />
                                    </IconButton>
                                </Stack>
                            </Box>

                        </Stack>
                    </Grid>
                </Grid>

                {/* Footer Insight */}
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontStyle: 'italic' }}>
                        Note: Direct printing significantly improves fulfillment speed by bypassing the operating system's print dialog.
                    </Typography>
                </Box>
            </SettingsContainer>
        </Box>
    );
};

export default QZTrayPage;